/**
 * Maps row data (strategy instance) to form data for pre-populating RPC forms.
 *
 * Mapping priority:
 * 1. Direct schema property name matches in original_data.parameters
 * 2. Common field mappings (bot_id, portfolio, accounts, etc.)
 * 3. Schema label field matching (legacy)
 *
 * @param rpcSchema - The RPC JSON schema with properties
 * @param selectedBotData - The selected row data (IAGAlgoDataRow)
 * @returns Form data object with pre-populated values
 */
export const computeDefaultFormDataFromBot = (
    rpcSchema: { properties?: Record<string, any> },
    selectedBotData: Record<string, unknown>
): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const props = rpcSchema?.properties;
    if (!props || typeof selectedBotData !== 'object' || selectedBotData === null) return out;

    // selectedBotData is the original_data (instanceState) from the row; parameters is a JSON string from the backend
    const rawParameters = selectedBotData.parameters;
    let parameters: Record<string, unknown> | undefined;
    if (typeof rawParameters === 'string') {
        try { parameters = JSON.parse(rawParameters); } catch { /* ignore */ }
    } else if (rawParameters && typeof rawParameters === 'object') {
        parameters = rawParameters as Record<string, unknown>;
    }

    for (const [propName, propSchema] of Object.entries(props)) {
        let value: unknown = undefined;

        // Special case: raw_config should contain the entire parameters object
        if (propName === 'raw_config' && parameters && Object.keys(parameters).length > 0) {
            value = parameters;
        }
        // Special case: fields is a top-level property that directly receives the parameters object (update RPCs like OtcAlgoParams)
        else if (propName === 'fields' && parameters && Object.keys(parameters).length > 0) {
            value = parameters;
        }
        // Special case: config wraps parameters under a 'fields' sub-property (update RPCs)
        else if (propName === 'config' && parameters && Object.keys(parameters).length > 0 && propSchema?.properties?.fields) {
            const configValue: Record<string, unknown> = {};
            // parameters may already be structured as { strategy_instance_id, fields: {...} }
            // In that case use parameters.fields directly to avoid double-nesting
            if (parameters.fields && typeof parameters.fields === 'object') {
                configValue.fields = parameters.fields;
                if (propSchema?.properties?.strategy_instance_id) {
                    configValue.strategy_instance_id = parameters.strategy_instance_id ?? selectedBotData.strategy_instance_id;
                }
            } else {
                configValue.fields = parameters;
                if (propSchema?.properties?.strategy_instance_id && selectedBotData.strategy_instance_id != null) {
                    configValue.strategy_instance_id = selectedBotData.strategy_instance_id;
                }
            }
            value = configValue;
        }
        // 1. Try direct match from parameters object
        else if (parameters && propName in parameters && parameters[propName] != null) {
            value = parameters[propName];
        }
        // 2. Try direct match from selectedBotData (the instanceState object)
        else if (propName in selectedBotData && selectedBotData[propName] != null) {
            value = selectedBotData[propName];
        }
        // 3. Handle common field mappings
        else if (propName === 'bot_id' || propName === 'strategy_instance_id') {
            value = selectedBotData.strategy_instance_id;
        }
        else if (propName === 'portfolio' || propName === 'portfolio_id') {
            value = selectedBotData.portfolio_id;
        }
        else if (propName === 'accounts' || propName === 'account_ids') {
            value = selectedBotData.account_ids;
        }
        else if (propName === 'strategy' || propName === 'strategy_id') {
            value = selectedBotData.strategy_id;
        }
        // 4. Try matching via schema label (legacy behavior)
        else {
            const label = propSchema?.label;
            if (label && label in selectedBotData && selectedBotData[label] != null) {
                value = selectedBotData[label];
            }
        }

        // Only set the value if we found something
        if (value !== undefined && value !== null) {
            out[propName] = value;
        }
    }

    return out;
}